/**
 * Loading placeholder for the report page — mirrors the real layout
 * (sub-tab strip → KPI card row → two chart panels → wide table panel)
 * so the page doesn't reflow when content arrives. Shown while the
 * review-gate status and the report data are being fetched.
 */

import type { ReactNode } from 'react';

function Bar({ className = '' }: { className?: string }) {
  return <div className={`ba-shimmer rounded-md ${className}`} />;
}

function PanelShell({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-border bg-background p-5 ${className}`}>
      {children}
    </div>
  );
}

export default function ReportSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading report">
      {/* Sub-tab strip */}
      <div className="border-b border-border">
        <div className="flex items-center gap-6 px-8 py-3">
          {['w-16', 'w-24', 'w-20', 'w-14', 'w-20'].map((w, i) => (
            <Bar key={i} className={`h-4 ${w}`} />
          ))}
          <div className="ml-auto flex gap-2">
            <Bar className="h-8 w-24 rounded-lg" />
            <Bar className="h-8 w-24 rounded-lg" />
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-6">
        {/* Headline + summary line */}
        <div className="space-y-2">
          <Bar className="h-6 w-64" />
          <Bar className="h-4 w-96 max-w-full" />
        </div>

        {/* KPI card row */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <PanelShell key={i}>
              <Bar className="h-3 w-20 mb-3" />
              <Bar className="h-7 w-28 mb-2" />
              <Bar className="h-3 w-16" />
            </PanelShell>
          ))}
        </div>

        {/* Two chart panels */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <PanelShell key={i}>
              <Bar className="h-4 w-40 mb-4" />
              <Bar className="h-48 w-full rounded-lg" />
            </PanelShell>
          ))}
        </div>

        {/* Wide table panel */}
        <PanelShell>
          <Bar className="h-4 w-48 mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Bar key={i} className="h-4 w-full" />
            ))}
          </div>
        </PanelShell>
      </div>
    </div>
  );
}
